/**
 * @fileoverview Module Registry for managing available modules
 * 
 * The ModuleRegistry is responsible for registering, unregistering, and
 * retrieving modules within the ParrotMate module system.
 */

import { ModuleStatus, ModuleContextType } from '../types/index.js';

/**
 * Registry for managing available modules in the system
 * 
 * @class ModuleRegistry
 */
export class ModuleRegistry {
  /**
   * Create a new ModuleRegistry instance
   */
  constructor() {
    /** @type {Map<string, ModuleRegistryEntry>} */
    this.modules = new Map();
    
    /** @type {Map<string, Set<string>>} */
    this.contextIndex = new Map();
    
    /** @type {Map<string, Set<string>>} */
    this.permissionIndex = new Map();
    
    /** @type {Map<string, Set<string>>} */
    this.dependencyIndex = new Map();
  }

  /**
   * Register a new module in the registry
   * 
   * @param {ModuleRegistryEntry} entry - The module entry to register
   * @throws {Error} If module ID already exists or entry is invalid
   */
  register(entry) {
    if (!entry || !entry.id) {
      throw new Error('Module entry must have a valid ID');
    }

    if (this.modules.has(entry.id)) {
      throw new Error(`Module with ID '${entry.id}' is already registered`);
    }

    // Validate required fields
    if (!entry.name || !entry.version || !entry.component || !entry.config) {
      throw new Error('Module entry is missing required fields (name, version, component, config)');
    }

    // Set default values
    const moduleEntry = {
      ...entry,
      dependencies: entry.dependencies || [],
      loadedAt: new Date(),
      status: entry.status || ModuleStatus.LOADED
    };

    // Store the module
    this.modules.set(entry.id, moduleEntry);

    // Update indexes
    this._updateContextIndex(entry.id, entry.config.context);
    this._updatePermissionIndex(entry.id, entry.config.permissions || []);
    this._updateDependencyIndex(entry.id, entry.dependencies || []);

    console.log(`Module '${entry.id}' registered successfully`);
  }

  /**
   * Unregister a module from the registry
   * 
   * @param {string} moduleId - The ID of the module to unregister
   * @returns {boolean} True if module was unregistered, false if not found
   */
  unregister(moduleId) {
    if (!this.modules.has(moduleId)) {
      return false;
    }

    const entry = this.modules.get(moduleId);
    
    // Remove from indexes
    this._removeFromContextIndex(moduleId, entry.config.context);
    this._removeFromPermissionIndex(moduleId, entry.config.permissions || []);
    this._removeFromDependencyIndex(moduleId, entry.dependencies || []);

    // Remove the module
    this.modules.delete(moduleId);

    console.log(`Module '${moduleId}' unregistered successfully`);
    return true;
  }

  /**
   * Get a module by its ID
   * 
   * @param {string} moduleId - The ID of the module to retrieve
   * @returns {ModuleRegistryEntry|undefined} The module entry or undefined if not found
   */
  get(moduleId) {
    return this.modules.get(moduleId);
  }

  /**
   * Get all registered modules
   * 
   * @returns {ModuleRegistryEntry[]} Array of all registered modules
   */
  getAll() {
    return Array.from(this.modules.values());
  }

  /**
   * Check if a module is registered
   * 
   * @param {string} moduleId - The ID of the module to check
   * @returns {boolean} True if module is registered, false otherwise
   */
  has(moduleId) {
    return this.modules.has(moduleId);
  }

  /**
   * Get modules by context (user, admin, shared)
   * 
   * @param {'user'|'admin'|'shared'} context - The context to filter by
   * @returns {ModuleRegistryEntry[]} Array of modules matching the context
   */
  getByContext(context) {
    const moduleIds = this.contextIndex.get(context) || new Set();
    return Array.from(moduleIds)
      .map(id => this.modules.get(id))
      .filter(Boolean);
  }

  /**
   * Get modules that require a specific permission
   * 
   * @param {string} permission - The permission to filter by
   * @returns {ModuleRegistryEntry[]} Array of modules requiring the permission
   */
  getByPermission(permission) {
    const moduleIds = this.permissionIndex.get(permission) || new Set();
    return Array.from(moduleIds)
      .map(id => this.modules.get(id))
      .filter(Boolean);
  }

  /**
   * Get modules that depend on a specific module
   * 
   * @param {string} dependencyId - The dependency module ID
   * @returns {ModuleRegistryEntry[]} Array of modules that depend on the specified module
   */
  getDependents(dependencyId) {
    const moduleIds = this.dependencyIndex.get(dependencyId) || new Set();
    return Array.from(moduleIds)
      .map(id => this.modules.get(id))
      .filter(Boolean);
  }

  /**
   * Get modules filtered by user permissions
   * 
   * @param {Permission[]} userPermissions - Array of user permissions
   * @returns {ModuleRegistryEntry[]} Array of modules the user can access
   */
  getAccessibleModules(userPermissions) {
    const userPermissionIds = new Set(userPermissions.map(p => p.id));
    
    return this.getAll().filter(module => {
      const requiredPermissions = module.config.permissions || [];
      return requiredPermissions.every(permission => 
        userPermissionIds.has(permission)
      );
    });
  }

  /**
   * Get module statistics
   * 
   * @returns {Object} Registry statistics
   */
  getStats() {
    const modules = this.getAll();
    const statusCounts = {};
    const contextCounts = {};

    modules.forEach(module => {
      statusCounts[module.status] = (statusCounts[module.status] || 0) + 1;
      contextCounts[module.config.context] = (contextCounts[module.config.context] || 0) + 1;
    });

    return {
      total: modules.length,
      byStatus: statusCounts,
      byContext: contextCounts,
      withDependencies: modules.filter(m => m.dependencies.length > 0).length,
      withPermissions: modules.filter(m => m.config.permissions.length > 0).length
    };
  }

  /**
   * Validate module dependencies
   * 
   * @param {string} moduleId - The module ID to validate
   * @returns {Object} Validation result with missing dependencies
   */
  validateDependencies(moduleId) {
    const module = this.get(moduleId);
    if (!module) {
      return { valid: false, error: 'Module not found' };
    }

    const missing = module.dependencies.filter(depId => !this.has(depId));
    
    return {
      valid: missing.length === 0,
      missing,
      satisfied: module.dependencies.filter(depId => this.has(depId))
    };
  }

  /**
   * Clear all modules from the registry
   */
  clear() {
    this.modules.clear();
    this.contextIndex.clear();
    this.permissionIndex.clear();
    this.dependencyIndex.clear();
    console.log('Module registry cleared');
  }

  // Private helper methods

  /**
   * Update the context index
   * @private
   */
  _updateContextIndex(moduleId, context) {
    if (!this.contextIndex.has(context)) {
      this.contextIndex.set(context, new Set());
    }
    this.contextIndex.get(context).add(moduleId);
  }

  /**
   * Update the permission index
   * @private
   */
  _updatePermissionIndex(moduleId, permissions) {
    permissions.forEach(permission => {
      if (!this.permissionIndex.has(permission)) {
        this.permissionIndex.set(permission, new Set());
      }
      this.permissionIndex.get(permission).add(moduleId);
    });
  }

  /**
   * Update the dependency index
   * @private
   */
  _updateDependencyIndex(moduleId, dependencies) {
    dependencies.forEach(dependency => {
      if (!this.dependencyIndex.has(dependency)) {
        this.dependencyIndex.set(dependency, new Set());
      }
      this.dependencyIndex.get(dependency).add(moduleId);
    });
  }

  /**
   * Remove from context index
   * @private
   */
  _removeFromContextIndex(moduleId, context) {
    const contextSet = this.contextIndex.get(context);
    if (contextSet) {
      contextSet.delete(moduleId);
      if (contextSet.size === 0) {
        this.contextIndex.delete(context);
      }
    }
  }

  /**
   * Remove from permission index
   * @private
   */
  _removeFromPermissionIndex(moduleId, permissions) {
    permissions.forEach(permission => {
      const permissionSet = this.permissionIndex.get(permission);
      if (permissionSet) {
        permissionSet.delete(moduleId);
        if (permissionSet.size === 0) {
          this.permissionIndex.delete(permission);
        }
      }
    });
  }

  /**
   * Remove from dependency index
   * @private
   */
  _removeFromDependencyIndex(moduleId, dependencies) {
    dependencies.forEach(dependency => {
      const dependencySet = this.dependencyIndex.get(dependency);
      if (dependencySet) {
        dependencySet.delete(moduleId);
        if (dependencySet.size === 0) {
          this.dependencyIndex.delete(dependency);
        }
      }
    });
  }
}

// Create and export a singleton instance
export const moduleRegistry = new ModuleRegistry();
